import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, CalendarDays, Ticket, TrendingUp, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AdminGallery } from "@/components/AdminGallery";
import { AdminSettings } from "@/components/admin/AdminSettings";
import { BookingsList } from "@/components/admin/BookingsList";
import { TicketsList } from "@/components/admin/TicketsList";
import { AdminReviewsWall } from "@/components/admin/AdminReviewsWall";
import { CustomersList } from "@/components/admin/CustomersList";
import { CustomerProfile } from "@/components/admin/CustomerProfile";
import { AdminGifts } from "@/components/admin/AdminGifts";
import { AdminMembers } from "@/components/admin/AdminMembers";
import { AdminJobs } from "@/components/admin/AdminJobs";

const TABS = ["bookings", "tickets", "jobs", "customers", "members", "gifts", "reviews", "gallery", "settings"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [customer, setCustomer] = useState(null);
  const [jobDraft, setJobDraft] = useState(null);
  const [summary, setSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [reviews, setReviews] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, b, t, r] = await Promise.all([api.get("/admin/summary"), api.get("/admin/bookings"), api.get("/admin/tickets"), api.get("/admin/reviews")]);
      setSummary(s.data); setBookings(b.data); setTickets(t.data); setReviews(r.data);
    } catch { toast.error("Failed to load admin data"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const logout = async () => { await api.post("/auth/logout"); nav("/admin/login"); };
  const updateBooking = async (id, status) => {
    const fd = new FormData(); fd.append("status", status);
    await api.patch(`/admin/bookings/${id}`, fd);
    toast.success("Booking updated"); load();
  };
  const saveNote = async (id, notes) => {
    const fd = new FormData(); fd.append("notes", notes);
    try { await api.patch(`/admin/bookings/${id}/notes`, fd); toast.success("Note saved"); load(); }
    catch { toast.error("Could not save note"); }
  };
  const sendRecap = async (id, summary) => {
    const fd = new FormData(); fd.append("summary", summary);
    try { const r = await api.post(`/admin/bookings/${id}/recap`, fd); toast.success(r.data.payment_link_sent ? "Recap + payment link emailed — booking marked completed" : "Recap emailed — booking marked completed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not send recap"); throw err; }
  };
  const requestPayment = async (id) => {
    try { await api.post(`/admin/bookings/${id}/request-payment`); toast.success("Payment link emailed to the customer"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not send the payment link"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <span className="dg-eyebrow">Admin</span>
          <h1 className="font-display text-3xl font-extrabold mt-1">Command center</h1>
        </div>
        <button onClick={logout} className="dg-btn-ghost rounded-full px-4 py-2 text-sm flex items-center gap-2" data-testid="admin-logout">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      {summary && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <Stat icon={CalendarDays} label="Total bookings" value={summary.total_bookings} />
          <Stat icon={Clock} label="Upcoming" value={summary.upcoming_bookings} />
          <Stat icon={Ticket} label="Open tickets" value={summary.open_tickets} />
          <Stat icon={TrendingUp} label="New this week" value={summary.new_this_week} />
        </div>
      )}

      <div className="flex gap-2 mb-6 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
            className={`shrink-0 px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border ${
              tab === t ? "bg-cyan-500 text-slate-900 border-cyan-500" : "bg-slate-900/50 text-slate-300 border-slate-700 hover:border-cyan-400"
            }`}>{t}{t === "reviews" && reviews.some((r) => r.status === "pending") ? <span className="ml-2 inline-block w-2 h-2 rounded-full bg-[#FFD600] align-middle" data-testid="admin-tab-reviews-dot" /> : null}</button>
        ))}
      </div>

      {tab === "bookings" && <BookingsList bookings={bookings} onStatus={updateBooking} onNote={saveNote} onRecap={sendRecap} onRequestPayment={requestPayment} onCustomer={(email) => { setCustomer(email); setTab("customers"); }} />}
      {tab === "tickets" && <TicketsList tickets={tickets} onChanged={load} onPostJob={(d) => { setJobDraft(d); setTab("jobs"); }} />}
      {tab === "jobs" && <AdminJobs draft={jobDraft} />}
      {tab === "customers" && (customer
        ? <CustomerProfile email={customer} bookings={bookings} tickets={tickets} onBack={() => setCustomer(null)} />
        : <CustomersList bookings={bookings} tickets={tickets} onOpen={setCustomer} />)}
      {tab === "gifts" && <AdminGifts />}
      {tab === "members" && <AdminMembers />}
      {tab === "reviews" && <AdminReviewsWall reviews={reviews} onChanged={load} />}
      {tab === "gallery" && <AdminGallery />}
      {tab === "settings" && <AdminSettings />}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="dg-card rounded-xl p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-lg bg-cyan-500/10 border border-cyan-500/40 grid place-items-center">
        <Icon className="w-5 h-5 text-cyan-400" />
      </div>
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-400">{label}</div>
        <div className="font-display text-2xl font-bold">{value}</div>
      </div>
    </div>
  );
}
